// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { useRouter } from 'expo-router';

import { Text } from '@/components/Themed';
import { FriendInviteQrButton } from '@/components/FriendInviteQrButton';
import GoffyInput from '@/components/GoffyInput';
import { useUser } from '@/app/user-context';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { usePlayerSearch } from '@/hooks/usePlayerSearch';
import { isCreatedByCurrentUser } from '@/services/playerPayload';
import appColors from '@/theme/colors';
import {
  type BffInboundMessage,
  type FriendListSnapshotEvent,
  type FriendSnapshot,
  getSharedBffWebSocket,
} from '@/services/bffWebSocket';

/**
 * The screen lists two kinds of people, which come from two different places: linked users arrive
 * over the friend websocket, guests are read from GET /api/players (the caller's own guests are
 * part of that payload). The filter picks which of the two to show.
 */
type FriendFilter = 'all' | 'linked' | 'guests';

/** A guest player this user created earlier — someone they added to a round without an account. */
type GuestPlayer = {
  playerId: string;
  name: string;
};

type PersonRow =
  | { kind: 'linked'; key: string; name: string; friend: FriendSnapshot }
  | { kind: 'guest'; key: string; name: string; playerId: string };

/**
 * A friend add/remove/update only fails via a *.rejected reply or this timeout — never via the
 * send() return value: 'queued' means the frame is flushed when the socket (re)opens and the
 * request is still in flight.
 */
const FRIEND_ACTION_TIMEOUT_MS = 12000;

const FILTERS: ReadonlyArray<{ key: FriendFilter; labelKey: string }> = [
  { key: 'all', labelKey: 'friends.filterAll' },
  { key: 'linked', labelKey: 'friends.filterLinked' },
  { key: 'guests', labelKey: 'friends.filterGuests' },
];

export default function FriendsScreen() {
  const { user } = useUser();
  const { t } = useI18n();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [friends, setFriends] = useState<FriendSnapshot[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FriendFilter>('all');
  const [guests, setGuests] = useState<GuestPlayer[]>([]);
  const [guestsFailed, setGuestsFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [nicknameModalFriendId, setNicknameModalFriendId] = useState<string | null>(null);
  const [nicknameModalValue, setNicknameModalValue] = useState('');
  const friendsRef = useRef<FriendSnapshot[]>([]);
  const pendingFriendAddRequestIdRef = useRef<string | null>(null);
  const addRollbackFriendsRef = useRef<FriendSnapshot[] | null>(null);
  const pendingRemoveFriendIdRef = useRef<string | null>(null);
  const removeRollbackFriendsRef = useRef<FriendSnapshot[] | null>(null);
  const addTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const removeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const socket = useMemo(() => getSharedBffWebSocket(apiBaseUrl), [apiBaseUrl]);
  const {
    results: searchResults,
    searching,
    failed: searchFailed,
  } = usePlayerSearch(apiBaseUrl, user?.token, searchQuery);

  const requestSnapshot = useCallback(() => {
    if (!user?.token) {
      return;
    }
    setLoading(true);
    socket.setAuthToken(user.token);
    socket.sendFriendListRequest();
  }, [socket, user?.token]);

  useEffect(() => {
    friendsRef.current = friends;
  }, [friends]);


  useEffect(() => {
    if (!user?.token) {
      return;
    }

    socket.setAuthToken(user.token);
    requestSnapshot();

    const unsubscribe = socket.onMessage((message: BffInboundMessage) => {
      const type = (message as { type?: string }).type;

      if (type === 'friend.list.snapshot') {
        const payload = message as FriendListSnapshotEvent;
        setFriends(payload.friends ?? []);
        setError(null);
        setLoading(false);
        setSaving(false);
        return;
      }

      if (type === 'friend.removed') {
        if (removeTimeoutRef.current) {
          clearTimeout(removeTimeoutRef.current);
          removeTimeoutRef.current = null;
        }
        const payload = message as { friendId?: string };
        if (payload.friendId) {
          setFriends((current) => current.filter((friend) => friend.friendId !== payload.friendId));
        }
        if (!payload.friendId || payload.friendId === pendingRemoveFriendIdRef.current) {
          pendingRemoveFriendIdRef.current = null;
          removeRollbackFriendsRef.current = null;
          setSaving(false);
        }
        requestSnapshot();
        return;
      }

      if (type === 'friend.added' || type === 'friend.updated') {
        if (type === 'friend.added') {
          if (addTimeoutRef.current) {
            clearTimeout(addTimeoutRef.current);
            addTimeoutRef.current = null;
          }
          pendingFriendAddRequestIdRef.current = null;
          addRollbackFriendsRef.current = null;
        } else if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
          updateTimeoutRef.current = null;
        }
        requestSnapshot();
        setSaving(false);
        return;
      }

      if (type === 'friend.add.rejected') {
        if (addTimeoutRef.current) {
          clearTimeout(addTimeoutRef.current);
          addTimeoutRef.current = null;
        }
        if (addRollbackFriendsRef.current) {
          setFriends(addRollbackFriendsRef.current);
        }
        pendingFriendAddRequestIdRef.current = null;
        addRollbackFriendsRef.current = null;
        setSaving(false);
        setLoading(false);
        const payload = message as { message?: string };
        setError(payload.message ?? t('friends.actionFailed'));
        return;
      }

      if (type === 'friend.remove.rejected') {
        if (removeTimeoutRef.current) {
          clearTimeout(removeTimeoutRef.current);
          removeTimeoutRef.current = null;
        }
        if (removeRollbackFriendsRef.current) {
          setFriends(removeRollbackFriendsRef.current);
        }
        pendingRemoveFriendIdRef.current = null;
        removeRollbackFriendsRef.current = null;
        setSaving(false);
        setLoading(false);
        const payload = message as { message?: string };
        setError(payload.message ?? t('friends.actionFailed'));
        return;
      }

      if (type === 'friend.update.rejected') {
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
          updateTimeoutRef.current = null;
        }
        setSaving(false);
        setLoading(false);
        const payload = message as { message?: string };
        setError(payload.message ?? t('friends.actionFailed'));
        return;
      }

    });

    return () => {
      unsubscribe();
    };
  }, [requestSnapshot, socket, t, user?.token]);

  // Unmount-only: the subscribe effect above re-runs whenever its deps change, so clearing the
  // action timeouts there would silently disarm a pending add/remove/update mid-flight.
  useEffect(
    () => () => {
      [addTimeoutRef, removeTimeoutRef, updateTimeoutRef].forEach((timeoutRef) => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      });
    },
    []
  );

  // Guests never reach the friend snapshot — the BFF drops friend rows that point at a player
  // without an account. GET /api/players is the surface that does return them: it answers with the
  // caller, their friends, and the guests they created, so filtering on "created by me" leaves
  // exactly the guests.
  useEffect(() => {
    if (!user?.token) {
      return;
    }

    let cancelled = false;

    const loadGuests = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/players`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          if (!cancelled) {
            setGuestsFailed(true);
          }
          return;
        }

        const payload = (await response.json()) as
          | Array<Record<string, unknown>>
          | { players?: Array<Record<string, unknown>> };
        const entries = Array.isArray(payload) ? payload : (payload.players ?? []);
        if (cancelled) {
          return;
        }

        setGuests(
          entries
            .filter((entry) => isCreatedByCurrentUser(entry, user.id))
            // AI opponents are also "created by me" but belong to round setup, not here.
            .filter((entry) => entry.isAi !== true && entry.IsAi !== true)
            .map((entry) => ({
              playerId: String(entry.id ?? entry.playerId ?? '').trim(),
              name: String(entry.displayName ?? entry.name ?? '').trim(),
            }))
            .filter((guest) => guest.playerId.length > 0 && guest.name.length > 0)
        );
        setGuestsFailed(false);
      } catch {
        if (!cancelled) {
          setGuestsFailed(true);
        }
      }
    };

    loadGuests();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, user?.id, user?.token]);

  const friendPlayerIdSet = useMemo(() => new Set(friends.map((friend) => friend.playerId)), [friends]);

  // The server applies each player's search-visibility setting, so this only drops people the
  // caller already has.
  const linkedCandidates = useMemo(
    () =>
      searchResults
        .filter((player) => player.userId && player.userId !== user?.id && !friendPlayerIdSet.has(player.id))
        .slice(0, 20),
    [friendPlayerIdSet, searchResults, user?.id]
  );

  const rows = useMemo<PersonRow[]>(() => {
    const linkedRows: PersonRow[] = friends.map((friend) => ({
      kind: 'linked',
      key: friend.friendId,
      name: friend.name,
      friend,
    }));
    const guestRows: PersonRow[] = guests.map((guest) => ({
      kind: 'guest',
      key: `guest-${guest.playerId}`,
      name: guest.name,
      playerId: guest.playerId,
    }));

    if (activeFilter === 'linked') {
      return linkedRows;
    }
    if (activeFilter === 'guests') {
      return guestRows;
    }
    return [...linkedRows, ...guestRows];
  }, [activeFilter, friends, guests]);

  const visibleRows = useMemo(() => {
    const normalized = globalSearchQuery.trim().toLowerCase();
    if (!normalized) {
      return rows;
    }
    return rows.filter((row) => {
      const name = (row.name ?? '').toLowerCase();
      const nickname = row.kind === 'linked' ? (row.friend.nickname ?? '').toLowerCase() : '';
      return name.includes(normalized) || nickname.includes(normalized);
    });
  }, [globalSearchQuery, rows]);

  const countLabel =
    activeFilter === 'linked' ? 'friends.countLinked' : activeFilter === 'guests' ? 'friends.countGuests' : 'friends.countAll';

  const addFriend = useCallback(
    (playerId: string, nickname?: string) => {
      if (!user?.token || !playerId) {
        return;
      }
      const requestId = generateRequestId();
      const linkedPlayer = searchResults.find((player) => player.id === playerId);
      const displayName = (nickname ?? '').trim() || linkedPlayer?.name?.trim() || '';
      addRollbackFriendsRef.current = friends;
      pendingFriendAddRequestIdRef.current = requestId;
      setFriends((current) => {
        if (current.some((friend) => friend.playerId === playerId)) {
          return current;
        }
        return [
          ...current,
          {
            friendId: `pending-${requestId}`,
            playerId,
            nickname: nickname?.trim() || null,
            name: displayName || t('friends.linked'),
            playerName: linkedPlayer?.name ?? null,
            userId: linkedPlayer?.userId ?? null,
            status: 'linked',
          },
        ];
      });
      setSaving(true);
      setError(null);
      socket.setAuthToken(user.token);
      socket.sendFriendAdd({ playerId, nickname, requestId });
      if (addTimeoutRef.current) {
        clearTimeout(addTimeoutRef.current);
      }
      addTimeoutRef.current = setTimeout(() => {
        addTimeoutRef.current = null;
        if (pendingFriendAddRequestIdRef.current !== requestId) {
          return;
        }
        if (addRollbackFriendsRef.current) {
          setFriends(addRollbackFriendsRef.current);
        }
        pendingFriendAddRequestIdRef.current = null;
        addRollbackFriendsRef.current = null;
        setSaving(false);
        setError(t('friends.actionFailed'));
      }, FRIEND_ACTION_TIMEOUT_MS);
    },
    [friends, searchResults, socket, t, user?.token]
  );

  const removeFriend = useCallback(
    (friendId: string) => {
      if (!user?.token || !friendId) {
        return;
      }
      if (friendId.startsWith('pending-')) {
        setFriends((current) => current.filter((friend) => friend.friendId !== friendId));
        setSaving(false);
        return;
      }
      removeRollbackFriendsRef.current = friends;
      pendingRemoveFriendIdRef.current = friendId;
      setFriends((current) => current.filter((friend) => friend.friendId !== friendId));
      setSaving(true);
      setError(null);
      socket.setAuthToken(user.token);
      socket.sendFriendRemove({ friendId });
      if (removeTimeoutRef.current) {
        clearTimeout(removeTimeoutRef.current);
      }
      removeTimeoutRef.current = setTimeout(() => {
        removeTimeoutRef.current = null;
        if (pendingRemoveFriendIdRef.current !== friendId) {
          return;
        }
        if (removeRollbackFriendsRef.current) {
          setFriends(removeRollbackFriendsRef.current);
        }
        pendingRemoveFriendIdRef.current = null;
        removeRollbackFriendsRef.current = null;
        setSaving(false);
        setError(t('friends.actionFailed'));
      }, FRIEND_ACTION_TIMEOUT_MS);
    },
    [friends, socket, t, user?.token]
  );

  const updateNickname = useCallback(
    (friendId: string, nickname: string) => {
      if (!user?.token || !friendId) {
        return;
      }
      setSaving(true);
      setError(null);
      socket.setAuthToken(user.token);
      socket.sendFriendUpdate({ friendId, nickname: nickname.trim() || undefined });
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      updateTimeoutRef.current = setTimeout(() => {
        updateTimeoutRef.current = null;
        setSaving(false);
        setError(t('friends.actionFailed'));
      }, FRIEND_ACTION_TIMEOUT_MS);
    },
    [socket, t, user?.token]
  );

  const openNicknameModal = useCallback((friend: FriendSnapshot) => {
    setNicknameModalFriendId(friend.friendId);
    setNicknameModalValue(friend.nickname ?? '');
  }, []);

  const closeNicknameModal = useCallback(() => {
    setNicknameModalFriendId(null);
    setNicknameModalValue('');
  }, []);

  const saveNicknameFromModal = useCallback(() => {
    if (!nicknameModalFriendId) {
      return;
    }
    updateNickname(nicknameModalFriendId, nicknameModalValue);
    closeNicknameModal();
  }, [closeNicknameModal, nicknameModalFriendId, nicknameModalValue, updateNickname]);

  const goBack = useCallback(() => {
    if (typeof router.canGoBack === 'function' && router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/my-tours');
  }, [router]);

  return (
    <>
      <ScrollView style={styles.screen} contentContainerStyle={styles.container}>
        <Pressable style={styles.backButton} onPress={goBack}>
          <Text style={styles.backButtonText}>{t('menu.back')}</Text>
        </Pressable>
        <Text style={styles.title}>{t('friends.title')}</Text>
        {loading ? <Text style={styles.note}>{t('friends.loading')}</Text> : null}
        {error ? <Text style={styles.error}>{error}</Text> : null}

        <FriendInviteQrButton style={styles.inviteRow} />

        <TextInput
          testID="friend-list-filter-input"
          value={globalSearchQuery}
          onChangeText={setGlobalSearchQuery}
          placeholder={t('friends.findPlayersPlaceholder')}
          placeholderTextColor="#8aa58f"
          style={styles.globalSearchInput}
        />

        <View style={styles.filterRow}>
          {FILTERS.map(({ key, labelKey }) => (
            <Pressable
              key={key}
              testID={`friend-filter-${key}`}
              accessibilityRole="button"
              accessibilityState={{ selected: activeFilter === key }}
              style={[styles.filterChip, activeFilter === key ? styles.filterChipActive : null]}
              onPress={() => setActiveFilter(key)}
            >
              <Text style={[styles.filterChipText, activeFilter === key ? styles.filterChipTextActive : null]}>
                {t(labelKey)}
              </Text>
            </Pressable>
          ))}
        </View>

        <View style={styles.inlinePanel}>
          <Text style={styles.addSectionTitle}>{t('friends.addLinked')}</Text>
          <GoffyInput
            testID="friend-search-input"
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder={t('friends.searchPlaceholder')}
            autoCapitalize="none"
            autoCorrect={false}
          />
          <Text style={styles.note}>{t('friends.searchHint')}</Text>
          {searching ? <Text style={styles.note}>{t('friends.searching')}</Text> : null}
          {searchFailed ? <Text style={styles.error}>{t('friends.searchFailed')}</Text> : null}
          {!searching && !searchFailed && searchQuery.trim().length > 0 && linkedCandidates.length === 0 ? (
            <Text style={styles.note}>{t('friends.searchNoResults')}</Text>
          ) : null}
          {linkedCandidates.map((candidate) => (
            <View key={candidate.id} style={styles.candidateRow}>
              <Text style={styles.name}>{candidate.name}</Text>
              <Pressable
                testID={`friend-add-${candidate.id}`}
                style={styles.actionButton}
                onPress={() => addFriend(candidate.id)}
              >
                <Text style={styles.actionText}>{t('friends.add')}</Text>
              </Pressable>
            </View>
          ))}
        </View>

        <View style={styles.friendsHeaderRow}>
          <Text style={styles.friendsHeader}>{t(countLabel, { count: rows.length })}</Text>
        </View>

        {guestsFailed && activeFilter !== 'linked' ? <Text style={styles.error}>{t('friends.guestsFailed')}</Text> : null}
        {rows.length === 0 ? (
          <Text style={styles.note}>{t(activeFilter === 'guests' ? 'friends.guestsEmpty' : 'friends.empty')}</Text>
        ) : null}
        {rows.length > 0 && visibleRows.length === 0 ? <Text style={styles.note}>{t('friends.noMatches')}</Text> : null}

        {visibleRows.map((row) => {
          if (row.kind === 'guest') {
            return (
              <View key={row.key} testID={`guest-row-${row.playerId}`} style={styles.friendCard}>
                <View style={styles.friendCardLeading}>
                  <View style={styles.avatarPlaceholder}>
                    <Text style={styles.avatarLetter}>{(row.name.trim().charAt(0) || '?').toUpperCase()}</Text>
                  </View>
                </View>
                <View style={styles.friendCardContent}>
                  <View style={styles.friendCardBody}>
                    <View style={styles.friendTitleRow}>
                      <Text style={styles.friendName} numberOfLines={1}>
                        {row.name}
                      </Text>
                      <View style={styles.guestBadge}>
                        <Text style={[styles.badgeText, styles.guestBadgeText]}>{t('friends.guest')}</Text>
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            );
          }

          const friend = row.friend;
          const normalizedName = (friend.name ?? '').trim().toLowerCase();
          const normalizedNickname = (friend.nickname ?? '').trim();
          const showNickname = normalizedNickname.length > 0 && normalizedNickname.toLowerCase() !== normalizedName;

          return (
            <View key={row.key} style={styles.friendCard}>
            <View style={styles.friendCardLeading}>
              <View style={styles.avatarPlaceholder}>
                <Text style={styles.avatarLetter}>{(friend.name?.trim()?.charAt(0) || '?').toUpperCase()}</Text>
              </View>
            </View>
            <View style={styles.friendCardContent}>
              <View style={styles.friendCardBody}>
                <View style={styles.friendTitleRow}>
                  <Text style={styles.friendName} numberOfLines={1}>
                    {friend.name}
                  </Text>
                  <View style={styles.badge}>
                    <Text style={styles.badgeText}>{t('friends.linked')}</Text>
                  </View>
                </View>
                {showNickname ? (
                  <Text style={styles.friendMeta} numberOfLines={1}>
                    {friend.nickname}
                  </Text>
                ) : null}
              </View>
              <View style={styles.friendActions}>
                <Pressable
                  testID={`friend-nickname-${friend.friendId}`}
                  style={styles.iconButton}
                  onPress={() => openNicknameModal(friend)}
                >
                  <Text style={styles.iconButtonText}>{t('friends.editNickname')}</Text>
                </Pressable>
                <Pressable
                  testID={`friend-remove-${friend.friendId}`}
                  style={styles.iconDangerButton}
                  onPress={() => removeFriend(friend.friendId)}
                >
                  <Text style={styles.iconButtonText}>{t('friends.remove')}</Text>
                </Pressable>
              </View>
            </View>
            </View>
          );
        })}
      </ScrollView>
      <Modal transparent visible={nicknameModalFriendId !== null} animationType="fade" onRequestClose={closeNicknameModal}>
        <Pressable style={styles.modalBackdrop} onPress={closeNicknameModal}>
          <Pressable style={styles.modalCard} onPress={() => {}}>
            <Text style={styles.modalTitle}>{t('friends.editNickname')}</Text>
            <GoffyInput
              value={nicknameModalValue}
              onChangeText={setNicknameModalValue}
              placeholder={t('friends.nicknamePlaceholder')}
              autoFocus
            />
            <View style={styles.modalActions}>
              <Pressable testID="friend-nickname-save" style={styles.actionButton} onPress={saveNicknameFromModal}>
                <Text style={styles.actionText}>{t('friends.saveNickname')}</Text>
              </Pressable>
              <Pressable style={styles.dangerButton} onPress={closeNicknameModal}>
                <Text style={styles.actionText}>{t('friends.cancel')}</Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: '#031907',
  },
  container: {
    flexGrow: 1,
    paddingHorizontal: 10,
    paddingTop: 8,
    gap: 10,
    backgroundColor: '#031907',
    paddingBottom: 24,
  },
  backButton: {
    alignSelf: 'flex-start',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#245932',
    backgroundColor: '#123b1f',
    paddingHorizontal: 12,
    height: 38,
    justifyContent: 'center',
  },
  backButtonText: {
    color: '#d4f5da',
    fontWeight: '700',
    fontSize: 13,
  },
  title: {
    color: '#d4f5da',
    fontSize: 26,
    fontWeight: '800',
  },
  globalSearchInput: {
    borderWidth: 1,
    borderColor: '#2d6025',
    borderRadius: 10,
    color: '#d4f5da',
    backgroundColor: '#0c110b',
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 13,
    letterSpacing: 1,
  },
  inviteRow: {
    justifyContent: 'flex-start',
  },
  filterRow: {
    flexDirection: 'row',
    gap: 8,
  },
  filterChip: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#214f2a',
    paddingHorizontal: 14,
    paddingVertical: 7,
    backgroundColor: '#0a220f',
  },
  filterChipActive: {
    borderColor: '#77d23a',
    backgroundColor: '#133316',
  },
  filterChipText: {
    color: '#34d69d',
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  filterChipTextActive: {
    color: '#b3ff75',
  },
  addSectionTitle: {
    color: '#d4f5da',
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
    lineHeight: 17,
  },
  inlinePanel: {
    borderWidth: 1,
    borderColor: '#1e5a2d',
    borderRadius: 12,
    padding: 8,
    backgroundColor: '#062111',
    gap: 6,
  },
  friendsHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  friendsHeader: {
    color: '#d6f4da',
    fontSize: 34,
    fontWeight: '900',
    letterSpacing: -1,
  },
  friendCard: {
    borderWidth: 1,
    borderColor: '#0f3c1f',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 8,
    backgroundColor: '#062411',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  friendCardLeading: {
    width: 44,
    alignItems: 'center',
  },
  avatarPlaceholder: {
    width: 38,
    height: 38,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#2f6a3d',
    backgroundColor: '#0b3a1b',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarLetter: {
    color: '#adf36a',
    fontSize: 14,
    fontWeight: '800',
  },
  friendCardBody: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  friendCardContent: {
    flex: 1,
    gap: 8,
    minWidth: 0,
  },
  friendTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  friendName: {
    color: '#d6f4da',
    fontSize: 17,
    fontWeight: '800',
    flex: 1,
    flexShrink: 1,
  },
  badge: {
    borderWidth: 1,
    borderColor: '#245c30',
    backgroundColor: '#13371b',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  guestBadge: {
    borderWidth: 1,
    borderColor: '#5c5324',
    backgroundColor: '#37311a',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  badgeText: {
    color: '#8fcf9c',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  guestBadgeText: {
    color: '#e0d08a',
  },
  friendMeta: {
    color: '#8fcf9c',
    fontSize: 12,
    fontWeight: '600',
  },
  friendActions: {
    flexDirection: 'row',
    gap: 6,
    flexWrap: 'wrap',
  },
  iconButton: {
    borderWidth: 1,
    borderColor: appColors.border.default,
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 5,
    backgroundColor: '#1e4d22',
  },
  iconDangerButton: {
    borderWidth: 1,
    borderColor: '#f87171',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 5,
    backgroundColor: '#7f1d1d',
  },
  iconButtonText: {
    color: appColors.text.primary,
    fontSize: 11,
    fontWeight: '700',
  },
  note: {
    color: '#9bcfa8',
    fontSize: 12,
  },
  error: {
    color: '#fecaca',
    fontSize: 12,
  },
  candidateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
    borderWidth: 1,
    borderColor: appColors.border.default,
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  name: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
    flex: 1,
  },
  actionButton: {
    borderWidth: 1,
    borderColor: appColors.border.default,
    borderRadius: 7,
    paddingHorizontal: 8,
    paddingVertical: 6,
    backgroundColor: '#1e4d22',
  },
  dangerButton: {
    borderWidth: 1,
    borderColor: '#f87171',
    borderRadius: 7,
    paddingHorizontal: 8,
    paddingVertical: 6,
    backgroundColor: '#7f1d1d',
  },
  actionText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    justifyContent: 'center',
    padding: 16,
  },
  modalCard: {
    borderWidth: 1,
    borderColor: '#2a6a36',
    borderRadius: 10,
    backgroundColor: '#0a2814',
    padding: 10,
    gap: 8,
  },
  modalTitle: {
    color: '#d4f5da',
    fontSize: 14,
    fontWeight: '700',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 6,
    justifyContent: 'flex-end',
  },
});
